import { format } from 'date-fns';
import { getValues } from '@shopify/react-form';
import sortBy from 'lodash/sortBy';
import getRealDate from '@sdk/utils/getRealDate';
import { TimesheetEntry, TimesheetEntryType } from '@sdk/timesheets/types/TimesheetEntry';
import { TimesheetProjection } from '@sdk/timesheets/types/TimesheetProjection';
import {
  TimesheetEntryPayloadEntry,
  TimesheetEntryPayloadDeletedEntry,
} from '@sdk/timesheets/api/apiTypes';
import { getTimesheetEntriesForProjection } from '@sdk/timesheets/helpers/utils';

import {
  EditFormFieldValues,
  EditFormFields,
  StartDateWithLocalTime,
} from '@web/timesheets/EditTimesheetModal/editTimesheetModal.types';
import { MaybeBreakAttestations } from '@web/timesheets/CreateTimesheetModal/createTimesheetModal.types';
import {
  getEmptyTimeClockEntry,
  filterTimesheetEntriesForForm,
} from '@web/timesheets/CreateTimesheetModal/utils/timeClockUtils';
import { mapTimesheetEntriesToTimeClockEntriesWithDates } from '@web/timesheets/EditTimesheetModal/utils/mappers';
import { ShiftWithLocationTimezone } from '@web/timeClock/ClockOutModal/clockOutModal.types';
import { EventStatus } from '@web/onboarding/Checklist/onboardingChecklist.types';

/**
 * Shopify React Form doesn't include dynamic list fields inside it's basic fields set.
 * This small helper function helps extract Create Timesheet Form field values.
 *
 * It's like shopify's `getValues` but with support for timeClock field inside dynamicList.
 *
 * @param formData
 * @returns
 */
export const getFieldValues = (formData: EditFormFields): EditFormFieldValues => ({
  ...getValues(formData.fields),
  timeClock: formData.dynamicLists.timeClock.value,
});

/**
 * Bulk edit endpoint expect only entries that got modified, deleted or added.
 * For shift that had multiple entries and only one is modified we need to filter out untouched ones.
 *
 * @param timesheetEntries
 * @param originalTimesheetEntries
 */
export const filterUnmodifiedEntires = <T extends TimesheetEntryPayloadEntry>(
  timesheetEntries: T[],
  originalTimesheetEntries: TimesheetEntry[]
): T[] =>
  timesheetEntries.filter((timesheetEntry) => {
    if (timesheetEntry.deleted) {
      // Don't include reverted changes from new entries (entries added during edit and then deleted)
      return Boolean(timesheetEntry.id);
    }

    if (!timesheetEntry.id) {
      // Always include new entries that haven't been marked deleted (previous check)
      return true;
    }

    const originalEntry = originalTimesheetEntries.find(({ id }) => id === timesheetEntry.id);
    const timestamp = getRealDate(timesheetEntry.timestamp);
    return originalEntry?.timestamp.getTime() !== timestamp.getTime();
  });

/**
 * If all clock-related timesheet entries have been deleted from a projection then make sure
 * to also delete status changes such as mark_pending or mark_approved
 *
 * @param timesheetEntries
 * @param originalTimesheetEntries
 */
export const deleteDanglingStatusChangeEntries = (
  timesheetEntries: TimesheetEntryPayloadEntry[],
  originalTimesheetEntries: TimesheetEntry[],
  projections: TimesheetProjection[]
): (TimesheetEntryPayloadEntry | TimesheetEntryPayloadDeletedEntry)[] => {
  const result: (TimesheetEntryPayloadEntry | TimesheetEntryPayloadDeletedEntry)[] = [
    ...timesheetEntries,
  ];

  const deletedEntryIds = new Set(
    timesheetEntries.filter((e) => e.deleted && e.id).map((e) => e.id)
  );

  projections.forEach((projection) => {
    const projectionEntries = getTimesheetEntriesForProjection(
      projection,
      originalTimesheetEntries
    );

    const entriesLeftAfterDeletion = projectionEntries.filter((e) => !deletedEntryIds.has(e.id));
    const managerApprovalStatusChangeTypes = [
      TimesheetEntryType.markApproved,
      TimesheetEntryType.markPending,
    ];

    const onlyStatusChangesLeft = entriesLeftAfterDeletion.every((e) =>
      managerApprovalStatusChangeTypes.includes(e.type)
    );

    if (onlyStatusChangesLeft) {
      entriesLeftAfterDeletion.forEach((entry) => {
        result.push({
          id: entry.id,
          deleted: true,
        });
      });
    }
  });

  return result;
};

/**
 * Returns date in ISO format but without timezone, example: 2022-09-18T22:00:00
 */
export const formatDateToTimestampString = (date: Date) =>
  `${format(date, 'yyyy-MM-dd')}T${format(date, 'HH:mm:ss')}`;

/**
 * Strip down timesheetProjections just to breakAttestions.
 * Returns null if no timesheetProjections has been passed.
 *
 * @param timesheetProjections
 * @returns
 */
export const getBreakAttestationsFromTimesheetProjections = (
  timesheetProjections?: TimesheetProjection[]
): MaybeBreakAttestations => {
  if (!timesheetProjections) {
    // there would be no timesheet projections when creating new timesheet
    return null;
  }

  return timesheetProjections.reduce((acc, { breakAttestations }) => {
    if (breakAttestations?.length) {
      acc.push(...breakAttestations);
    }
    return acc;
  }, []);
};

/**
 * Helper method to get fields for time clock sections when editing shift.
 */
export const getTimeClockEntriesForDynamicList = (timesheetEntries: TimesheetEntry[]) => {
  const filteredTimesheetEntries = filterTimesheetEntriesForForm(timesheetEntries);

  // Empty entries happen when you edit timesheet, coming from shift, that hasn't yet been interacted with
  if (!filteredTimesheetEntries?.length) {
    return [getEmptyTimeClockEntry()];
  }

  return mapTimesheetEntriesToTimeClockEntriesWithDates(filteredTimesheetEntries);
};

export const extractStartDateWithLocalTime = ({
  dtstart,
  dtstartLocal,
}: StartDateWithLocalTime): StartDateWithLocalTime => ({
  dtstart,
  dtstartLocal,
});

export const getShiftPayload = (
  shift: ShiftWithLocationTimezone,
  rawTimesheetPayload: TimesheetEntryPayloadEntry[]
) => {
  const filteredClockInEntries = rawTimesheetPayload.filter(
    ({ type, deleted }) => type === TimesheetEntryType.clockIn && !deleted
  );
  const [firstClockInEntry] = sortBy(filteredClockInEntries, [
    ({ timestamp }) => getRealDate(timestamp),
  ]);

  return {
    id: shift.id,
    dtend: firstClockInEntry.timestamp,
    dtstart: firstClockInEntry.timestamp,
    user: { id: shift.user.id },
    position: { id: shift.position.id },
    location: { id: shift.location.id },
    status: EventStatus.published,
    summary: shift.summary,
  };
};
